/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Sebastian Hartmann <s.hartmann@techdivision.com>
 */

import PropTypes from 'prop-types';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';

/**
 * Custom useStudioAutoComplete
 * @param props
 */
export function useStudioAutoComplete(props) {
  const {
    studios, maps, map, setSelectedMarker, studioUrls,
  } = props;
  const [buttonDisabled, setButtonDisabled] = useState(null);
  const [studioId, setStudioId] = useState(null);
  const searchReference = useRef(null);
  const zoomLevel = 12;

  /**
     * Handle the studio search on the google map after clicking the map button
     */
  const handleSearchStudio = (event) => {
    event.preventDefault();
    const request = {
      query: searchReference.current.value,
      fields: ['name', 'geometry'],
    };
    const service = new maps.places.PlacesService(map);
    service.findPlaceFromQuery(request, (results, status) => {
      if (status === maps.places.PlacesServiceStatus.OK && results) {
        map.setCenter(results[0].geometry.location);
        map.setZoom(zoomLevel);
        const foundStudio = studios.find((studio) => studio.studioName === searchReference.current.value);
        if (foundStudio) {
          setSelectedMarker(foundStudio);
        }
      }
    });
  };

  /**
     * Handles the input change on the Autocomplete to disable the studio link button if no studio data is given
     */
  const handleAutoCompleteChange = (e, val) => {
    const studioData = studios.find((studio) => studio.studioName === val) || {};
    if (studioData) {
      setStudioId(studioData.id);
      setButtonDisabled(false);
    } else {
      setStudioId(null);
      setButtonDisabled(true);
    }
  };

  /**
     * Returns the studio url
     */
  const getStudioUrl = useCallback(() => {
    if (studioId) {
      return studioUrls[studioId];
    }
    return null;
  }, [studioId, studioUrls]);

  /**
     * Returns the studio url
     */
  useEffect(() => {
    if (getStudioUrl()) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
  }, [getStudioUrl]);

  /**
     * Handles the click on the studio button and opens a new browser tab
     */
  const handleGoToStudioPage = () => {
    if (getStudioUrl()) {
      window.location.href = getStudioUrl();
    }
  };

  return {
    handleSearchStudio,
    handleGoToStudioPage,
    handleAutoCompleteChange,
    searchReference,
    buttonDisabled,
  };
}

useStudioAutoComplete.propTypes = {
  handleSearchStudio: PropTypes.func.isRequired,
  handleGoToStudioPage: PropTypes.func.isRequired,
  map: PropTypes.object.isRequired,
  maps: PropTypes.object.isRequired,
};
